import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Container, Skeleton, Stack, Text, Title, UnstyledButton } from '@mantine/core';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { Link, useNavigate, useParams } from 'react-router-dom';
import type { Correction, CorrectionAction, Suggestion } from '@nelnela/contracts';
import { reanchor } from '@nelnela/corrections';
import { useStudy } from '../app/study';
import { api, ApiError } from '../lib/api';
import { ERROR_RULE } from '../lib/error-tint';
import {
  applyAccept,
  changedSince,
  checkRetryPlan,
  correctionAtCaret,
  editedAgo,
  editorSurfaceStyle,
  entryLoadProblem,
  entryTitle,
  insertSuggestion,
  looksNotGeorgian,
  mirrorRuns,
  normalizeDraft,
  placementsFor,
  saveRetryDelayMs,
  shouldCheck,
  statusWord,
  type JournalApi,
  type MirrorRun,
  type Placement,
} from '../lib/journal';
import { startActivity, type ActivityHandle } from '../lib/session';
import { ulid } from '../lib/ulid';
import { useLiveProfile } from '../lib/use-profile';
import { CorrectionCard } from '../components/CorrectionCard';
import { KeyboardBox } from '../components/KeyboardBox';
import { IconBack } from '../components/watch/icons';
import { TRANSCRIPT_SIZE_DEFAULT } from './me-settings';

/**
 * F12 Journal — write, and get the language back (design `8d`).
 *
 * Two screens: `/journal` lists what has been written, `/journal/:compositionId` is the editor.
 *
 * **The editor is a `<textarea>` over a mirror, and that is the milestone's hardest call
 * (M6c step 0b).** The textarea keeps the Georgian IME, the caret, autocorrect, selection and
 * native undo — all correct for free, and all of which a `contenteditable` re-breaks on Android,
 * where this app is used. Under it sits an `aria-hidden` div at an identical box whose text is
 * `color: transparent` and whose only job is to paint the underlines. The two nodes wear ONE
 * style object (`editorSurfaceStyle`) because they only agree while they wrap identically.
 *
 * **The correction card is opened by the caret, not by a hit test.** `8d` docks it above the
 * suggestion strip rather than anchoring it to the word, which is what makes that possible: on
 * click/select/keyup we read `selectionStart` and show the card for the correction whose span
 * contains it. That is more accurate than testing a tap against a 1.5 px underline, it works from
 * a keyboard, and it means the mirror needs no pointer events and steals no touch.
 *
 * **The third mark, and how the two-mark rule survives it.** `reading-surface-spec.md` §2 allows
 * exactly two marks (dotted = weak word, solid = chunk) and the brief says "no traffic-light
 * colors on text, ever". A correction underline in the error tint is a third. It is allowed here
 * because this is the learner's OWN text and because it is alone: this surface draws no weak-word
 * dots at all, so a corrected word wears one mark, not two a millimetre apart. On your own
 * sentence what is *wrong* matters more than what is *shaky*.
 */

/**
 * `lib/api.ts`'s journal methods, checked against the interface this screen was built to.
 *
 * The screen was written before the client methods existed and reached them through a cast; the
 * cast is gone, and this `satisfies` is what replaces it. It is not decoration: `api` is a
 * hand-written object literal with sixty-odd methods and no generated types, so a rename on that
 * side would otherwise be caught by nothing until the screen threw
 * "compositionUpdate is not a function" in a browser.
 */
const journalApi: JournalApi = api;

// ---------------------------------------------------------------------------------------------
// /journal — the list
// ---------------------------------------------------------------------------------------------

export function Journal() {
  const study = useStudy();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const profile = study.me.profiles[0] ?? null;
  const [busy, setBusy] = useState(false);
  const [calm, setCalm] = useState<string | null>(null);

  const entries = useQuery({
    queryKey: ['compositions'],
    queryFn: () => journalApi.compositionList({ limit: 30 }),
  });

  const create = useCallback(async () => {
    if (!profile) return;
    setBusy(true);
    setCalm(null);
    try {
      const c = await journalApi.compositionCreate({
        targetLang: profile.targetLang,
        baseLang: profile.baseLang,
      });
      void queryClient.invalidateQueries({ queryKey: ['compositions'] });
      navigate(`/journal/${c.id}`);
    } catch (err) {
      setBusy(false);
      setCalm(calmLine(err, 'The entry didn’t open. Try again in a moment.'));
    }
  }, [navigate, profile, queryClient]);

  const items = entries.data?.items ?? null;

  return (
    <Container size={640} py="lg">
      <Stack gap="md">
        <Title order={2}>Journal</Title>

        <div style={panel}>
          <div style={{ fontSize: 14, color: 'var(--nn-text-strong)' }}>Write a few sentences</div>
          <Text fz={11.5} c="var(--nn-muted)" mt={6}>
            {/* Rule 2 of the milestone, said where it is relevant rather than in a policy page. */}
            In Georgian, about whatever you like. Corrections come back as soft underlines. What you
            write here stays yours — it is never shared and never used for anyone else’s material.
          </Text>
          <div style={{ marginTop: 11, display: 'flex', gap: 10, alignItems: 'center' }}>
            <UnstyledButton
              disabled={busy || !study.entitled}
              onClick={() => void create()}
              style={{ ...emberButton, opacity: busy || !study.entitled ? 0.5 : 1 }}
            >
              {busy ? 'Opening…' : 'New entry'}
            </UnstyledButton>
            {/* `compositionList` is `session`, not `entitled`: a learner whose trial has ended can
                still read everything they wrote. Only writing more is gated, so only the button
                is. */}
            {!study.entitled && (
              <span style={{ fontSize: 11, color: 'var(--nn-faint)' }}>
                Writing is part of the paid layer — what you wrote is still here.
              </span>
            )}
          </div>
          {calm && (
            <Text fz={11.5} c="var(--nn-muted)" mt={10} role="status">
              {calm}
            </Text>
          )}
        </div>

        {entries.isPending ? (
          <Stack gap={8}>
            <Skeleton height={44} radius="sm" />
            <Skeleton height={44} radius="sm" />
          </Stack>
        ) : entries.isError || !items || items.length === 0 ? (
          <Text fz={12} c="var(--nn-faint)">
            {entries.isError
              ? 'Your entries will be here when the connection is.'
              : 'Nothing written yet.'}
          </Text>
        ) : (
          <Stack gap={8}>
            {items.map((c) => (
              <UnstyledButton
                key={c.id}
                component={Link}
                to={`/journal/${c.id}`}
                style={{
                  display: 'block',
                  background: 'var(--nn-panel)',
                  border: '1px solid var(--nn-hairline)',
                  borderRadius: 9,
                  padding: '10px 13px',
                }}
              >
                <div lang="ka" style={{ fontSize: 13.5, color: 'var(--nn-text-strong)' }}>
                  {entryTitle(c)}
                </div>
                <div style={{ fontSize: 10.5, color: 'var(--nn-faint)', marginTop: 3 }}>
                  edited {editedAgo(c.updatedAt)}
                </div>
              </UnstyledButton>
            ))}
          </Stack>
        )}
      </Stack>
    </Container>
  );
}

// ---------------------------------------------------------------------------------------------
// /journal/:compositionId — the editor
// ---------------------------------------------------------------------------------------------

/** Autosave is a `PATCH` with no model call behind it (`contracts:1811`), so it can be eager. */
const AUTOSAVE_MS = 1_200;
/** How often the check gate is asked. Half a second is under the eye's notice and over a timer's. */
const GATE_TICK_MS = 500;

export function JournalEntry() {
  const { compositionId } = useParams<{ compositionId: string }>();
  const study = useStudy();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const profile = useLiveProfile(study.me.profiles[0] ?? null);
  const fontSizePx = profile?.display?.transcriptSize ?? TRANSCRIPT_SIZE_DEFAULT;

  const entryQ = useQuery({
    queryKey: ['composition', compositionId],
    queryFn: () => journalApi.compositionGet(compositionId!),
    enabled: !!compositionId,
  });

  const [text, setText] = useState('');
  const [corrections, setCorrections] = useState<Correction[]>([]);
  const [suggestions, setSuggestions] = useState<Suggestion[]>([]);
  /** the draft the last check was made against — the debounce measures distance from this */
  const [checkedText, setCheckedText] = useState('');
  const [acted, setActed] = useState<ReadonlyMap<string, CorrectionAction>>(() => new Map());
  const [taken, setTaken] = useState<string[]>([]);
  const [caret, setCaret] = useState(0);
  const [saving, setSaving] = useState(false);
  const [checking, setChecking] = useState(false);
  const [calm, setCalm] = useState<string | null>(null);
  /** a refusal (429) stops the automatic loop for this visit; the entry keeps saving */
  const [paused, setPaused] = useState(false);
  /**
   * The automatic check gave up after `CHECK_BACKOFF_MS`'s last wait, and the learner may ask for
   * one by hand. Separate from `paused`, which is a refusal: this one has a button (M6c fix 16).
   */
  const [stalled, setStalled] = useState(false);
  /** the last autosave did not land — drives the header's `not saved` (M6c fix 14) */
  const [saveFailed, setSaveFailed] = useState(false);
  /** bumped by the save retry timer, purely to re-arm the debounce effect below */
  const [saveNonce, setSaveNonce] = useState(0);
  const [hiddenCardId, setHiddenCardId] = useState<string | null>(null);
  const [scriptNoteDismissed, setScriptNoteDismissed] = useState(false);

  const taRef = useRef<HTMLTextAreaElement | null>(null);
  const seededRef = useRef(false);
  const savedTextRef = useRef('');
  const lastTypedAtRef = useRef(0);
  /**
   * Checks spent this VISIT, not this day: nothing persists it across a reload, so re-entering an
   * entry hands the client twenty more. That is deliberate — the authoritative cap is the
   * server's, which answers 429 with copy the learner can read (M6c step 0e), and a client that
   * tried to be the authority would need a store, a date rollover and a clock it can trust.
   */
  const checksTodayRef = useRef(0);
  const pendingCaretRef = useRef<number | null>(null);
  /** consecutive failed checks, reset by a check that comes back or by the learner asking again */
  const checkFailuresRef = useRef(0);
  /** epoch ms before which the gate must not fire again — the backoff `checkRetryPlan` decided */
  const checkWaitUntilRef = useRef(0);
  /** consecutive failed autosaves, reset by one that lands */
  const saveFailuresRef = useRef(0);
  const saveRetryRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  /** false once this editor has unmounted, so a timer cannot set state on a screen that is gone */
  const aliveRef = useRef(true);

  // Read by the gate's interval, which is installed once and must not see a stale closure.
  const textRef = useRef(text);
  textRef.current = text;
  const checkedRef = useRef(checkedText);
  checkedRef.current = checkedText;
  const checkingRef = useRef(checking);
  checkingRef.current = checking;
  const pausedRef = useRef(paused);
  pausedRef.current = paused;
  const stalledRef = useRef(stalled);
  stalledRef.current = stalled;
  const takenRef = useRef(taken);
  takenRef.current = taken;
  const caretRef = useRef(caret);
  caretRef.current = caret;

  // ------------------------------------------------ what the server already knows
  useEffect(() => {
    const data = entryQ.data;
    if (seededRef.current || !data) return;
    seededRef.current = true;
    // Normalised on the way in as well as on the way through `onType`, so that the seed and every
    // keystroke after it produce the same string for the same entry. The server already stores the
    // normalised form (`PATCH` normalises before writing `current_text`), so this is a no-op for
    // anything this app wrote — and it means `savedTextRef` starts out equal to `text`, which is
    // what keeps opening an entry from firing a pointless autosave.
    const seed = normalizeDraft(data.currentText, 0).text;
    setText(seed);
    savedTextRef.current = seed;
    setCorrections(data.corrections);
    setSuggestions(data.suggestions);
    // Seeded with the current draft, so that opening an entry does not spend a check on text that
    // has already been checked — EXCEPT at version 0, which means no check has ever been made
    // against this entry. Seeding that case with the draft too would leave a learner who wrote a
    // paragraph, left within three seconds of their last keystroke and came back with a page of
    // Georgian and no underlines, and no way to ask for any. `version` is derived server-side
    // from `composition_version` (step 1 decision 2), so it cannot be wrong about this.
    setCheckedText(data.version === 0 ? '' : seed);
  }, [entryQ.data]);

  // ------------------------------------------------ the activity
  // `compositionCheck` requires an `activityId` — the cost of the call is ledgered against it, and
  // the `production_use` events it writes hang off it. So the editor opens one `journal` activity
  // for the visit, exactly as `Read.tsx` opens a `story` one, and ends it on the way out.
  const activityRef = useRef<ActivityHandle | null>(null);
  useEffect(() => {
    if (!compositionId) return;
    let cancelled = false;
    let handle: ActivityHandle | null = null;
    startActivity(study.session, 'journal', { compositionId })
      .then((a) => {
        if (cancelled) {
          void a.end();
          return;
        }
        handle = a;
        activityRef.current = a;
      })
      .catch(() => console.warn('[journal] activityStart failed; a check has nothing to hang on'));
    return () => {
      cancelled = true;
      activityRef.current = null;
      if (handle) void handle.end();
    };
  }, [compositionId, study.session]);
  useEffect(() => {
    const onHide = () => {
      void activityRef.current?.end({ keepalive: true });
    };
    window.addEventListener('pagehide', onHide);
    return () => window.removeEventListener('pagehide', onHide);
  }, []);

  // ------------------------------------------------ autosave
  const save = useCallback(
    async (draft: string, keepalive = false) => {
      if (!compositionId || draft === savedTextRef.current) return;
      setSaving(true);
      try {
        await journalApi.compositionUpdate(
          compositionId,
          { text: draft },
          keepalive ? { keepalive: true } : undefined,
        );
        savedTextRef.current = draft;
        saveFailuresRef.current = 0;
        setSaveFailed(false);
        // A retry armed by an earlier failure has nothing left to do. Harmless if it fires (the
        // effect below returns immediately when the draft is already saved), but a timer left to
        // wake a live screen up a minute later for nothing is the kind of thing that gets blamed
        // for something else.
        if (saveRetryRef.current) {
          clearTimeout(saveRetryRef.current);
          saveRetryRef.current = null;
        }
        // The list's "edited N ago" is now wrong; it re-reads on next visit rather than now.
        void queryClient.invalidateQueries({ queryKey: ['compositions'] });
      } catch {
        // Still deliberately quiet about the CONTENT — never the draft, never a red line every
        // few seconds on a flaky train connection. What was wrong was being quiet about the FACT:
        // the catch used to do nothing but this warn, so `statusWord` went on saying `saved` for
        // text that was on no server, and the retry the old comment promised ("the next
        // keystroke's timer") only existed for a learner who kept typing. Someone who stopped sat
        // on a screen reading `saved` that was not. Now the header says `not saved` and the timer
        // below re-arms itself with a growing wait, for as long as the editor is open.
        console.warn('[journal] autosave failed');
        saveFailuresRef.current += 1;
        setSaveFailed(true);
        if (saveRetryRef.current) clearTimeout(saveRetryRef.current);
        if (aliveRef.current) {
          saveRetryRef.current = setTimeout(
            () => setSaveNonce((n) => n + 1),
            saveRetryDelayMs(saveFailuresRef.current),
          );
        }
      } finally {
        setSaving(false);
      }
    },
    [compositionId, queryClient],
  );

  // `saveNonce` is a dependency and nothing else: the retry timer bumps it so this effect re-runs
  // and schedules the same save again, which is the one thing a `[text]`-keyed debounce cannot do
  // for a learner who has stopped typing.
  useEffect(() => {
    if (!seededRef.current || text === savedTextRef.current) return;
    const t = setTimeout(() => void save(text), AUTOSAVE_MS);
    return () => clearTimeout(t);
  }, [text, save, saveNonce]);

  // Read by the unmount flush, which is installed once and must not be re-installed when `save`
  // changes identity — a cleanup that ran mid-visit would fire a PATCH the learner did not pause
  // for.
  const saveRef = useRef(save);
  saveRef.current = save;

  /**
   * The flush `pagehide` cannot do.
   *
   * **The bug:** the debounce effect's cleanup ran `clearTimeout` and nothing else, so tapping the
   * back chevron inside `AUTOSAVE_MS` cancelled the pending PATCH and the last 1.2 s of writing
   * was gone — while the header said `saved` the whole time. `pagehide` did not cover it: it
   * fires on document unload and bfcache, not on a react-router route change, and its listener is
   * removed by the same unmount. Every in-app exit from this screen took that path — the back
   * chevron, and the correction card's `lesson →` link.
   *
   * No `keepalive` here, unlike the `pagehide` save: an unmount is an in-app navigation, the page
   * is still alive and an ordinary fetch completes, and `keepalive` would put a long entry against
   * the 64 KB keepalive quota for no reason. If a PATCH is already in flight for this same draft
   * this sends a second identical one; the route is a plain idempotent UPDATE
   * (`compositions.ts:852`), and losing a save to be clever about a duplicate is the wrong trade.
   */
  useEffect(() => {
    aliveRef.current = true;
    return () => {
      aliveRef.current = false;
      if (saveRetryRef.current) clearTimeout(saveRetryRef.current);
      if (textRef.current !== savedTextRef.current) void saveRef.current(textRef.current);
    };
  }, []);

  // A draft in flight when the tab goes away is the common case, not the rare one, so this save
  // goes out with `keepalive` — the browser finishes it after the page is gone, which an ordinary
  // fetch on `pagehide` is not guaranteed to do. Nothing CHECKED is ever lost either way (the
  // check route carries the whole text in its own body); what this protects is the typing since
  // the last autosave.
  useEffect(() => {
    const onHide = () => {
      if (textRef.current !== savedTextRef.current) void save(textRef.current, true);
    };
    window.addEventListener('pagehide', onHide);
    return () => window.removeEventListener('pagehide', onHide);
  }, [save]);

  // ------------------------------------------------ the check
  const runCheck = useCallback(async () => {
    const draft = textRef.current;
    const activityId = activityRef.current?.id;
    // No activity yet (the POST is still in flight, or it failed): wait rather than send a check
    // whose cost and events would have nothing to hang off. The gate will ask again in 500 ms.
    if (!compositionId || !activityId) return;
    setChecking(true);
    try {
      const takenSuggestionIds = takenRef.current;
      const res = await journalApi.compositionCheck(compositionId, {
        text: draft,
        activityId,
        ...(takenSuggestionIds.length ? { takenSuggestionIds } : {}),
      });
      setCorrections(res.corrections);
      setSuggestions(res.suggestions);
      setCheckedText(draft);
      setTaken([]);
      checksTodayRef.current += 1;
      checkFailuresRef.current = 0;
      checkWaitUntilRef.current = 0;
      setCalm(null);
    } catch (err) {
      // The refusals worth naming, and nothing else. `err.kind` only — never the draft.
      //
      // **The bug:** this catch used to fall through to a calm line and change nothing the gate
      // reads, so the gate's next tick — 500 ms later — found all three conditions still true and
      // re-fired. Forever. On a 502 that is a Sonnet call and a committed ledger row every half
      // second, which reaches the day's whole conversational budget (journal AND chat, the gate
      // covers both purposes) in about two minutes. `countChecksToday` cannot stop it either: the
      // failed attempt's `composition_version` row is rolled back, so it is not counted.
      const status = err instanceof ApiError ? err.problem.status : null;
      const detail = err instanceof ApiError ? err.problem.detail : null;
      checkFailuresRef.current += 1;
      const plan = checkRetryPlan({ failures: checkFailuresRef.current, status });
      if (plan.retry === 'never') {
        setPaused(true);
        setCalm(
          detail ||
            (status === 403
              ? 'Corrections are part of the paid layer.'
              : 'The AI helper is resting for a bit. Your entry is saved.'),
        );
      } else if (plan.retry === 'byHand') {
        console.warn('[journal] check failed; the automatic loop is stopping');
        setStalled(true);
        setCalm('The check isn’t coming back. Your entry is saved — ask again when you like.');
      } else {
        console.warn('[journal] check failed');
        checkWaitUntilRef.current = Date.now() + plan.waitMs;
        setCalm('The check didn’t come back. Your entry is saved — trying again shortly.');
      }
    } finally {
      setChecking(false);
    }
  }, [compositionId]);

  /**
   * The way back from `stalled`, and the only thing that shortens a backoff.
   *
   * A keystroke deliberately does NOT clear the wait. It was the obvious alternative and it is
   * wrong: `checkedText` is untouched by a failure, so the changed-characters condition is already
   * satisfied, and resetting on typing would hand a learner writing through an outage one paid
   * attempt per three-second pause — the same loop, on a longer period.
   */
  const checkAgain = useCallback(() => {
    checkFailuresRef.current = 0;
    checkWaitUntilRef.current = 0;
    setStalled(false);
    setCalm(null);
    void runCheck();
  }, [runCheck]);

  // The gate is polled rather than scheduled: the rule is three conditions, only one of which is
  // a clock (`shouldCheck`), and a `setTimeout` chain would have to be torn down and rebuilt on
  // every keystroke to keep the other two honest. One interval, and the policy stays a pure
  // function that a test can ask questions of.
  useEffect(() => {
    const t = setInterval(() => {
      if (pausedRef.current || stalledRef.current) return;
      // The backoff a failed check earned. It is enforced here rather than inside `shouldCheck`
      // because it is not a policy about the draft — it is a policy about the server, and mixing
      // the two would make the gate's table of "does this spend money" answer two questions.
      if (Date.now() < checkWaitUntilRef.current) return;
      const ready = shouldCheck({
        idleMs: Date.now() - lastTypedAtRef.current,
        changedChars: changedSince(checkedRef.current, textRef.current),
        checksToday: checksTodayRef.current,
        inFlight: checkingRef.current,
      });
      if (ready) void runCheck();
    }, GATE_TICK_MS);
    return () => clearInterval(t);
  }, [runCheck]);

  // ------------------------------------------------ what to draw
  // `reanchor` is the shared implementation, never a copy: the same function the server's own
  // spans came out of. Recomputed per render because the draft moves under the corrections on
  // every keystroke — this is O(corrections × text) with corrections capped at 8.
  const placements = useMemo(
    () => placementsFor(reanchor(corrections, text), acted),
    [corrections, text, acted],
  );
  const runs = useMemo(() => mirrorRuns(text, placements), [text, placements]);
  const atCaret = correctionAtCaret(placements, caret);
  // Edit closes the card and leaves the caret inside the span, which would otherwise re-open it
  // on the very next render. The suppression is released as soon as the caret is somewhere else.
  const active = atCaret && atCaret.correction.id === hiddenCardId ? null : atCaret;
  useEffect(() => {
    if (hiddenCardId && (!atCaret || atCaret.correction.id !== hiddenCardId)) setHiddenCardId(null);
  }, [atCaret, hiddenCardId]);

  // ------------------------------------------------ the caret, after an edit we made
  useEffect(() => {
    const to = pendingCaretRef.current;
    if (to === null) return;
    pendingCaretRef.current = null;
    const ta = taRef.current;
    if (!ta) return;
    ta.focus();
    ta.setSelectionRange(to, to);
    setCaret(to);
  }, [text]);

  const markActed = useCallback((id: string, action: CorrectionAction) => {
    setActed((prev) => {
      const next = new Map(prev);
      next.set(id, action);
      return next;
    });
  }, []);

  const act = useCallback((id: string, action: CorrectionAction) => {
    // Fire and forget, idempotent on the client's ULID. The learner's screen has already moved;
    // a failed `correctionAct` costs one row in the event log and nothing they can see, and
    // blocking the button on it would make Accept feel broken on a slow connection.
    void journalApi
      .correctionAct(id, { action, eventId: ulid() })
      .catch(() => console.warn('[journal] correctionAct failed'));
  }, []);

  const onAccept = useCallback(
    (p: Placement<Correction>) => {
      const edit = applyAccept(textRef.current, p.span, p.correction.suggested);
      setText(edit.text);
      pendingCaretRef.current = edit.caret;
      // The gate's clock means "the learner has stopped for three seconds", and taking a fix is
      // the learner doing something. Without this, an Accept can be followed instantly by a check
      // paid for out of an idle period the learner was not actually idle for.
      lastTypedAtRef.current = Date.now();
      markActed(p.correction.id, 'accepted');
      act(p.correction.id, 'accepted');
    },
    [act, markActed],
  );

  const onIgnore = useCallback(
    (p: Placement<Correction>) => {
      markActed(p.correction.id, 'ignored');
      act(p.correction.id, 'ignored');
    },
    [act, markActed],
  );

  const onEdit = useCallback(
    (p: Placement<Correction>) => {
      // The caret goes to the END of the span, not around it. Selecting the word would mean the
      // learner's first keystroke deletes it — and Edit is chosen precisely by someone who wants
      // to keep most of the word and change its ending, which in Georgian is where the mistake
      // usually is.
      setHiddenCardId(p.correction.id);
      const ta = taRef.current;
      if (ta) {
        ta.focus();
        ta.setSelectionRange(p.span.charEnd, p.span.charEnd);
        setCaret(p.span.charEnd);
      }
      act(p.correction.id, 'edited');
    },
    [act],
  );

  const onSuggestion = useCallback((s: Suggestion) => {
    const edit = insertSuggestion(textRef.current, caretRef.current, s.text);
    setText(edit.text);
    pendingCaretRef.current = edit.caret;
    lastTypedAtRef.current = Date.now();
    // Remembered for the NEXT check: the server turns a taken suggestion into
    // `support.hintUsed: true` on that sentence's `production_use` events, which is what caps the
    // grade at `good` (M6c step 5). A phrase you were handed is recognition, not production.
    setTaken((prev) => (prev.includes(s.id) ? prev : [...prev, s.id]));
  }, []);

  /**
   * The one place the textarea's own string becomes the draft, and the fix for the coordinate bug.
   *
   * Every offset in this screen — `reanchor`'s spans, `mirrorRuns`, `correctionAtCaret`,
   * `applyAccept` — is an index into the NFC form, because that is what the server locates against
   * and what it stores. The draft used to be whatever the textarea held, so a single decomposed
   * accent anywhere in the entry moved every underline one place left and made Accept splice at the
   * wrong index. Normalising HERE, at the only boundary the raw string crosses, is what keeps one
   * string in play everywhere; `normalizeDraft` carries the caret across with it, since a caret is
   * an index into the string it was read from.
   *
   * The `pendingCaretRef` write is only for the rare keystroke that actually composed something:
   * React is about to write a value the textarea does not hold, and a browser handed a different
   * value parks the caret at the end of it. On every ordinary keystroke the two strings are equal
   * and nothing touches the selection.
   */
  const onType = useCallback((next: string, caretAt: number) => {
    lastTypedAtRef.current = Date.now();
    const draft = normalizeDraft(next, caretAt);
    setText(draft.text);
    setCaret(draft.caret);
    if (draft.text !== next) pendingCaretRef.current = draft.caret;
  }, []);

  const status = statusWord({ saving, checking, failed: saveFailed });
  const openChips = suggestions.filter((s) => !taken.includes(s.id));
  const showScriptNote = !scriptNoteDismissed && looksNotGeorgian(text);

  if (entryQ.isPending) {
    return (
      <div style={screen}>
        <Bar onBack={() => navigate('/journal')} status="saved" />
        <div style={{ padding: '14px 20px', display: 'flex', flexDirection: 'column', gap: 12 }}>
          {['92%', '84%', '70%'].map((w) => (
            <Skeleton key={w} height={17} width={w} radius="sm" />
          ))}
        </div>
      </div>
    );
  }
  if (!entryQ.data) {
    // Which failure this actually was. `retry: false` on the query client means one 500 or one
    // offline second lands here, and the copy used to call every one of them a possible deletion
    // of the learner's own diary.
    const failure = entryLoadProblem(
      entryQ.error instanceof ApiError ? entryQ.error.problem.status : null,
    );
    return (
      <div style={{ padding: '18px 18px 0' }}>
        <Text fz={13} c="var(--nn-muted)">
          {failure.line}
        </Text>
        <div style={{ display: 'flex', gap: 9, marginTop: 12 }}>
          {failure.retryable && (
            <UnstyledButton
              onClick={() => void entryQ.refetch()}
              style={{ ...ghostButton, display: 'inline-flex' }}
            >
              Try again
            </UnstyledButton>
          )}
          <UnstyledButton
            onClick={() => navigate('/journal')}
            style={{ ...ghostButton, display: 'inline-flex' }}
          >
            Back to Journal
          </UnstyledButton>
        </div>
      </div>
    );
  }

  return (
    <KeyboardBox style={screen}>
      <Bar onBack={() => navigate('/journal')} status={status} />

      <JournalEditorSurface
        text={text}
        runs={runs}
        fontSizePx={fontSizePx}
        textareaRef={taRef}
        onChange={onType}
        onCaret={setCaret}
      />

      {showScriptNote && (
        <div style={{ padding: '0 16px 8px' }}>
          {/* `Review.tsx`'s calm inline offer, in the one form that is true here: there is no
              transliterator client-side and no route that would accept one, so the honest thing
              to say is what happens, not what to do about it. */}
          <span style={{ fontSize: 11.5, color: 'var(--nn-muted)' }}>
            Not typing Georgian right now? It still saves — corrections only read Georgian.
          </span>{' '}
          <UnstyledButton
            onClick={() => setScriptNoteDismissed(true)}
            style={{ fontSize: 11.5, color: 'var(--nn-link)', display: 'inline' }}
          >
            got it
          </UnstyledButton>
        </div>
      )}

      {calm && (
        <div style={{ padding: '0 16px 8px' }} role="status">
          <Text fz={11.5} c="var(--nn-muted)">
            {calm}
          </Text>
          {/* Only when the automatic loop has stopped after repeated failures. A refusal (429,
              403) gets no button: the answer would be the same one, and offering a retry against
              a budget that is spent is a way of making a learner spend their attention instead. */}
          {stalled && (
            <UnstyledButton
              onClick={checkAgain}
              style={{
                ...ghostButton,
                marginTop: 8,
                fontSize: 11.5,
                padding: '5px 11px',
              }}
            >
              Check again
            </UnstyledButton>
          )}
        </div>
      )}

      {active && (
        <CorrectionCard
          correction={active.correction}
          onAccept={() => onAccept(active)}
          onEdit={() => onEdit(active)}
          onIgnore={() => onIgnore(active)}
        />
      )}

      {openChips.length > 0 && <SuggestionStrip chips={openChips} onPick={onSuggestion} />}
    </KeyboardBox>
  );
}

// ---------------------------------------------------------------------------------------------
// the editing surface: a textarea over a mirror
// ---------------------------------------------------------------------------------------------

export interface JournalEditorSurfaceProps {
  text: string;
  runs: MirrorRun[];
  /** the learner's Georgian size (16–18) — reaches BOTH nodes as `--nn-transcript-size` */
  fontSizePx: number;
  textareaRef?: React.MutableRefObject<HTMLTextAreaElement | null>;
  /**
   * The new value AND the caret it was read with, in one call.
   *
   * They travel together because the caret is an index into that exact string and the owner
   * normalises it (`normalizeDraft`): reporting the two separately let the screen hold an index
   * into a string it had already replaced, which is one character of drift per composed pair.
   */
  onChange: (text: string, caret: number) => void;
  onCaret: (caret: number) => void;
}

/**
 * The two nodes that have to agree.
 *
 * Exported so a node test can render it without a router or a query client and assert the one
 * thing that cannot be checked by reading: that the textarea and the mirror carry the same
 * `--nn-transcript-size`. That is the drift this component cannot tolerate — a single pixel of
 * difference and every underline in a long entry ends up under the wrong word — and it is the
 * kind of bug that looks fine on the machine it was written on.
 *
 * The two nodes below differ in exactly one thing that is not ink: the mirror is `overflow: hidden`
 * and the textarea is `overflow-y: auto`. That pair used to make the boxes different WIDTHS on
 * every platform with classic scrollbars, and every underline below the first re-wrapped line went
 * with it. `editorSurfaceStyle`'s `scrollbar-width: none` is what makes the pair harmless; the
 * test compares the two rendered styles declaration by declaration so it cannot come back.
 */
export function JournalEditorSurface({
  text,
  runs,
  fontSizePx,
  textareaRef,
  onChange,
  onCaret,
}: JournalEditorSurfaceProps) {
  const mirrorRef = useRef<HTMLDivElement | null>(null);
  const ownRef = useRef<HTMLTextAreaElement | null>(null);
  const ta = textareaRef ?? ownRef;
  const surface = editorSurfaceStyle(fontSizePx);

  const sync = useCallback(() => {
    const box = ta.current;
    const mirror = mirrorRef.current;
    if (!box || !mirror) return;
    mirror.scrollTop = box.scrollTop;
    mirror.scrollLeft = box.scrollLeft;
  }, [ta]);
  // Also after a programmatic edit (Accept, a chip): the textarea's scroll can move without an
  // `onScroll` when its content changes, and an unsynced mirror puts every underline a line out.
  useEffect(sync, [sync, text, runs]);

  return (
    <div style={{ position: 'relative', flex: 1, minHeight: 0 }}>
      <div
        ref={mirrorRef}
        aria-hidden
        lang="ka"
        style={{
          ...surface,
          overflow: 'hidden',
          color: 'transparent',
          pointerEvents: 'none',
          userSelect: 'none',
        }}
      >
        {runs.map((r, i) =>
          r.correctionId ? (
            <span key={`${r.correctionId}:${i}`} style={r.stale ? staleUnderline : underline}>
              {r.text}
            </span>
          ) : (
            <span key={`t:${i}`}>{r.text}</span>
          ),
        )}
        {/* A textarea reserves a line box after a trailing newline and a bare div does not, so
            without this the mirror is one line short the moment the learner presses Enter — and
            from there every underline below the fold is a line high. Zero width, so it can never
            wrap or shift anything. */}
        {'\u200b'}
      </div>
      <textarea
        ref={ta}
        className="nn-journal-input"
        lang="ka"
        aria-label="Your entry"
        placeholder="Write in Georgian…"
        spellCheck={false}
        value={text}
        // One call, both facts: `onChange` may replace the string it is handed (the draft is held
        // NFC-normalised), and a caret reported separately would be an index into the string that
        // was replaced. The owner sets the caret from what it kept.
        onChange={(e) => onChange(e.currentTarget.value, e.currentTarget.selectionStart)}
        // The three ways a caret moves that the card has to follow: a tap, a drag-select, and the
        // arrow keys. `onSelect` fires for all three in every browser this app supports, and the
        // other two are belt and braces for the Android WebView, where a tap that does not change
        // the selection sometimes does not fire it.
        onSelect={(e) => onCaret(e.currentTarget.selectionStart)}
        onClick={(e) => onCaret(e.currentTarget.selectionStart)}
        onKeyUp={(e) => onCaret(e.currentTarget.selectionStart)}
        onScroll={sync}
        style={{
          ...surface,
          background: 'transparent',
          color: 'var(--nn-text-strong)',
          caretColor: 'var(--nn-accent)',
          outline: 'none',
          resize: 'none',
          overflowY: 'auto',
          // A reading surface forbids selection; an editor IS selection (step 0b). The rule
          // applies to the mirror, which is a reading surface, and not to this node.
          userSelect: 'text',
        }}
      />
    </div>
  );
}

/**
 * The underline, inline rather than in `JOURNAL_CSS`.
 *
 * `READER_CSS`'s precedent says class rules belong in the exported stylesheet, and the placeholder
 * rule below is there for exactly that reason. This one is not, because it is the feature: if
 * `App.tsx` ever failed to concatenate the stylesheet the underlines would vanish silently and
 * the journal would look like it simply found no mistakes. An inline style cannot be forgotten.
 *
 * 1.5 px, `skip-ink: none` and a `.2em` offset are `8d`'s own values, and the thickness is not
 * novel — `READING_SURFACE_CSS` already draws both existing marks at 1.5 px under the
 * accessibility variant. The colour is `ERROR_RULE`: the coral-red error tint, never the accent
 * (reading-surface-spec §7 — the accent means "this is yours", and painting a learner's mistake
 * with the colour that means "yours" would be a small cruelty).
 */
const underline: React.CSSProperties = {
  textDecorationLine: 'underline',
  textDecorationStyle: 'solid',
  textDecorationColor: ERROR_RULE,
  textDecorationThickness: '1.5px',
  textUnderlineOffset: '.2em',
  textDecorationSkipInk: 'none',
};

/** Dealt with: still visible, no longer live. `8d`: "stale corrections fade". */
const staleUnderline: React.CSSProperties = { ...underline, opacity: 0.45 };

// ---------------------------------------------------------------------------------------------
// the card and the strip
// ---------------------------------------------------------------------------------------------

/** "You might also say" — `8d`'s two chips, docked above the keyboard, inserting at the caret. */
function SuggestionStrip({
  chips,
  onPick,
}: {
  chips: Suggestion[];
  onPick: (s: Suggestion) => void;
}) {
  return (
    <div
      className="nn-journal-strip"
      style={{
        display: 'flex',
        gap: 7,
        padding: '0 14px calc(10px + env(safe-area-inset-bottom))',
        overflowX: 'auto',
        flexShrink: 0,
      }}
    >
      {chips.map((s) => (
        <UnstyledButton
          key={s.id}
          lang="ka"
          onClick={() => onPick(s)}
          style={{
            fontSize: 12,
            padding: '5px 11px',
            borderRadius: 999,
            border: '1px solid color-mix(in srgb, var(--nn-text) 12%, transparent)',
            color: 'var(--nn-text)',
            whiteSpace: 'nowrap',
            flexShrink: 0,
          }}
        >
          {s.text}
        </UnstyledButton>
      ))}
    </div>
  );
}

// ---------------------------------------------------------------------------------------------
// chrome
// ---------------------------------------------------------------------------------------------

function Bar({ onBack, status }: { onBack: () => void; status: string }) {
  return (
    <div style={bar}>
      <UnstyledButton onClick={onBack} aria-label="Back" style={barButton}>
        <IconBack />
      </UnstyledButton>
      <Text fz={13.5} fw={600}>
        Journal
      </Text>
      <span style={{ flex: 1 }} />
      <span
        style={{ fontSize: 10.5, color: 'var(--nn-faint)', padding: '0 14px' }}
        role="status"
        aria-live="polite"
      >
        {status}
      </span>
    </div>
  );
}

function calmLine(err: unknown, fallback: string): string {
  if (err instanceof ApiError) {
    const { status, detail } = err.problem;
    if (status === 403) return detail || 'Writing is part of the paid layer.';
    if (status === 429) return detail || 'The AI helper is resting for a bit.';
    if (detail) return detail;
    const field = err.problem.errors?.[0]?.message;
    if (status === 422 && field) return field;
  }
  return fallback;
}

// ---------------------------------------------------------------------------------------------
// shared surfaces (the same four objects `Read.tsx` uses; kept local rather than exported from
// there, which would make a screen a module other screens import)
// ---------------------------------------------------------------------------------------------

const screen: React.CSSProperties = {
  height: 'calc(100dvh - var(--app-shell-footer-offset, 0rem))',
  display: 'flex',
  flexDirection: 'column',
  background: 'var(--nn-bg)',
};

const bar: React.CSSProperties = {
  display: 'flex',
  alignItems: 'center',
  height: 44,
  padding: '0 4px',
  gap: 2,
  flexShrink: 0,
  minWidth: 0,
};

const barButton: React.CSSProperties = {
  width: 40,
  height: 40,
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  color: 'var(--nn-text)',
  flexShrink: 0,
};

const panel: React.CSSProperties = {
  background: 'var(--nn-panel)',
  border: '1px solid var(--nn-hairline)',
  borderRadius: 12,
  padding: 16,
};

const emberButton: React.CSSProperties = {
  display: 'inline-flex',
  alignItems: 'center',
  justifyContent: 'center',
  padding: '8px 14px',
  borderRadius: 7,
  background: 'var(--nn-accent)',
  color: 'var(--nn-on-accent)',
  fontSize: 12.5,
  fontWeight: 600,
};

const ghostButton: React.CSSProperties = {
  display: 'inline-flex',
  alignItems: 'center',
  justifyContent: 'center',
  padding: '8px 14px',
  borderRadius: 7,
  border: '1px solid color-mix(in srgb, var(--nn-text) 12%, transparent)',
  color: 'var(--nn-text)',
  fontSize: 12.5,
};

/**
 * The rules that cannot be inline, to be concatenated into `App.tsx`'s single `<style>` beside
 * `READER_CSS`. **That wiring is the integrator's, not this step's** — without it the placeholder
 * takes the browser's default grey and the chip strip shows a scrollbar on desktop. Everything
 * load-bearing (the underline, the two boxes' geometry) is inline for exactly that reason.
 *
 * The last rule is the older half of a fix that mostly lives inline. `editorSurfaceStyle` sets
 * `scrollbar-width: none` on BOTH boxes, which is what actually keeps them the same width; this
 * covers the engines that never implemented that property (WebKit, and Blink before 121), where a
 * classic scrollbar in the textarea would otherwise narrow it by ~15 px while the mirror stayed
 * full width and every underline below the first re-wrapped line moved to the wrong word. It is
 * safe for it to be missing — those engines are the ones that overlay their scrollbars — which is
 * exactly why it may live here and the shared declaration may not.
 */
export const JOURNAL_CSS = `
.nn-journal-input::placeholder { color: var(--nn-faint); }
.nn-journal-strip { scrollbar-width: none; }
.nn-journal-strip::-webkit-scrollbar { display: none; }
.nn-journal-input::-webkit-scrollbar { display: none; }
`;
